import { FC } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";

import { formatCacheAge } from "../../utils/formatCacheAge";
import { formatDateTime } from "../../services/formatters";

interface CacheStatusBannerProps {
  cachedAt: string | null;
  isRefreshing: boolean;
  refreshProgress: number;
  dataUpdated: boolean;
  onRefresh: () => void;
  isCapturingScreenshot?: boolean;
}

const CacheStatusBanner: FC<CacheStatusBannerProps> = ({
  cachedAt,
  isRefreshing,
  refreshProgress,
  dataUpdated,
  onRefresh,
  isCapturingScreenshot = false,
}) => {
  const { t } = useTranslation();

  if (!cachedAt) return null;

  return (
    <Tooltip
      title={
        <>
          {t("cache_status.tooltip_moodle")}
          <br />
          <br />
          {t("cache_status.tooltip_refresh")}
        </>
      }
      arrow
      slotProps={{
        popper: {
          sx: {
            zIndex: 2002,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          padding: "4px 8px 4px 12px",
          backgroundColor: dataUpdated ? "rgba(76, 175, 80, 0.12)" : "rgba(33, 150, 243, 0.08)",
          color: dataUpdated ? "success.main" : "primary.main",
          borderRadius: "12px",
          border: dataUpdated ? "1px solid rgba(76, 175, 80, 0.3)" : "1px solid rgba(33, 150, 243, 0.2)",
          fontSize: "0.75rem",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <InfoIcon sx={{ fontSize: "14px", opacity: 0.7, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
          {isRefreshing
            ? t("cache_status.refreshing", { progress: refreshProgress })
            : isCapturingScreenshot
              ? t("cache_status.data_calculated_at", { time: formatDateTime(cachedAt) })
              : t("cache_status.full_data_calculated", { age: formatCacheAge(cachedAt) })}
        </Typography>
        {!isCapturingScreenshot && (
          <Box
            component="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
              marginLeft: "4px",
              border: "none",
              borderRadius: "50%",
              backgroundColor: "transparent",
              color: "primary.main",
              cursor: isRefreshing ? "not-allowed" : "pointer",
              opacity: isRefreshing ? 0.5 : 1,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: isRefreshing ? "transparent" : "rgba(33, 150, 243, 0.15)",
              },
              "&:active": {
                transform: isRefreshing ? "none" : "scale(0.95)",
              },
            }}
            title={isRefreshing ? String(t("cache_status.loading")) : String(t("cache_status.refresh_data"))}
          >
            <RefreshIcon
              sx={{
                fontSize: "16px",
                animation: isRefreshing ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default CacheStatusBanner;
